import { BOARD_SIZE, HAND_SIZE } from "./helpers";
import type {
  BoardTile,
  HandTiles,
  TileGenerationContext,
  TileValue,
  TileGenerator
} from "./types";

export type BoardCreationResult = {
  board: BoardTile[];
  nextBoardTileId: number;
};

const EARLY_BOARD_ATTEMPTS = 8;
const DEFAULT_BOARD_ATTEMPTS = 5;

function generatorContext(
  reason: TileGenerationContext["reason"],
  roundNumber: number,
  state: TileGenerationContext["state"]
): TileGenerationContext {
  return { reason, roundNumber, state };
}

function generateBoardCandidate(
  generator: TileGenerator,
  roundNumber: number,
  state: TileGenerationContext["state"]
): TileValue[] {
  return Array.from({ length: BOARD_SIZE }, () =>
    generator.nextTile(generatorContext("board", roundNumber, state))
  );
}

function boardFriendlinessScore(board: TileValue[], roundNumber: number): number {
  const average = board.reduce((sum, value) => sum + value, 0) / board.length;
  const lowCount = board.filter((value) => value <= 3).length;
  const midCount = board.filter((value) => value <= 6).length;
  const highCount = board.filter((value) => value >= 7).length;
  const extremeHighCount = board.filter((value) => value >= 8).length;

  let score =
    lowCount * 1.45 +
    midCount * 0.8 -
    highCount * 1.1 -
    extremeHighCount * 1.5 -
    Math.max(0, average - 5.2) * 1.45;

  if (roundNumber <= 3) {
    score += lowCount * 0.25 - highCount * 0.35;
  }

  return score;
}

function targetBoardScore(roundNumber: number): number {
  if (roundNumber <= 3) {
    return 4.7;
  }

  if (roundNumber <= 8) {
    return 3.8;
  }

  return 3;
}

function asDealtBoardTiles(
  values: TileValue[],
  createdTurn: number,
  nextBoardTileId: number
): BoardCreationResult {
  const board = values.map((value, index) => ({
    id: `b-${nextBoardTileId + index}`,
    value,
    source: "dealt" as const,
    createdTurn,
    buildDepth: 0
  }));

  return {
    board,
    nextBoardTileId: nextBoardTileId + board.length
  };
}

export function createBoardTiles(
  generator: TileGenerator,
  roundNumber: number,
  state: TileGenerationContext["state"],
  nextBoardTileId: number
): BoardCreationResult {
  const attemptLimit = roundNumber <= 3 ? EARLY_BOARD_ATTEMPTS : DEFAULT_BOARD_ATTEMPTS;
  let bestCandidate = generateBoardCandidate(generator, roundNumber, state);
  let bestScore = boardFriendlinessScore(bestCandidate, roundNumber);
  const threshold = targetBoardScore(roundNumber);
  const createdTurn = state?.turnIndex ?? 0;

  if (bestScore >= threshold) {
    return asDealtBoardTiles(bestCandidate, createdTurn, nextBoardTileId);
  }

  for (let attempt = 1; attempt < attemptLimit; attempt += 1) {
    const candidate = generateBoardCandidate(generator, roundNumber, state);
    const candidateScore = boardFriendlinessScore(candidate, roundNumber);

    if (candidateScore > bestScore) {
      bestCandidate = candidate;
      bestScore = candidateScore;
    }

    if (candidateScore >= threshold) {
      return asDealtBoardTiles(candidate, createdTurn, nextBoardTileId);
    }
  }

  return asDealtBoardTiles(bestCandidate, createdTurn, nextBoardTileId);
}

export function createHandTiles(
  generator: TileGenerator,
  roundNumber: number,
  state: TileGenerationContext["state"]
): HandTiles {
  return Array.from({ length: HAND_SIZE }, () =>
    generator.nextTile(generatorContext("hand", roundNumber, state))
  ) as HandTiles;
}
